import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { $gitStatus } from "../../git/git-repository.atom";
import { NoDocumentIdError, UnknownError, WorkflowError } from "./errors";
import {
	$commitChanges,
	$initializeGitRepo,
	$syncMainToCollab,
	initialCommit,
} from "./mutations.atom";
import { $syncAction, $syncActionWithLogging, $syncError, $syncState } from "./sync-state.atom";
import type { SyncAction, SyncSnapshot } from "./sync.types";

const logger = new Logger("commit.workflow.atom");

const setAction = (set: any, documentId: string, action: SyncAction | undefined) => {
	set($syncActionWithLogging, {
		documentId,
		action,
		workflow: "commit",
	});
};

export const $executeCommitWorkflow = atom(
	null,
	async (
		_get,
		set,
		input: {
			snap: SyncSnapshot;
			commitMessage?: string;
		}
	) => {
		const documentId = input.snap.documentId;

		try {
			const { snap, commitMessage } = input;

			if (!documentId) {
				throw new NoDocumentIdError("commit");
			}

			set($syncState(documentId), { isSyncing: true, workflow: "commit" });

			// Initialize repo if needed.
			if (!snap.gitRepoStatus) {
				logger.debug("Initializing git repository");
				setAction(set, documentId, "initialize-git-repo");
				await set($initializeGitRepo, { documentId });
				setAction(set, documentId, "commit-initial-changes");
				await initialCommit(set, { documentId });
				logger.debug("Git repository initialized");
			}

			// Commit outstanding changes if any.
			if (!snap.repoClean) {
				setAction(set, documentId, "commit-outstanding-changes");
				let message;
				if (commitMessage) {
					message = commitMessage.trim();
				}
				if (!message || message.length === 0) {
					message = `Committed changes on ${new Date().toLocaleString()}`;
				}
				await set($commitChanges, { documentId, message });

				// When on collab/main, sync the main bookmark to the new described
				// commit so that main vs moment/main ahead/behind reflects the
				// unpushed changes and the UI prompts to push.
				if (snap.isOnCollabBranch) {
					try {
						setAction(set, documentId, "sync-main-to-collab");
						await set($syncMainToCollab, documentId);
					} catch (syncError) {
						logger.warn("Failed to sync main to collab after commit", { syncError });
					}
				}
			}

			// CLEANUP
			set($syncError(documentId), null);
			set($gitStatus(documentId)); // refrech git status after commit
			logger.debug("Commit workflow completed");
		} catch (error) {
			logger.error("Error executing commit workflow", {
				error,
				snap: input.snap,
			});

			if (documentId) {
				if (!(error instanceof WorkflowError)) {
					set($syncError(documentId), new UnknownError("commit", error));
				} else {
					set($syncError(documentId), error);
				}
			}
		} finally {
			if (documentId) {
				set($syncState(documentId), { isSyncing: false, workflow: undefined });
				setAction(set, documentId, undefined);
				set($gitStatus(documentId)); // refrech git status after commit
			}
		}
	}
);
$executeCommitWorkflow.debugLabel = "executeCommitWorkflow";
